import React, { useState, useEffect, useCallback } from 'react';
import { LogIn, LogOut, Upload, Trash2, Eye, EyeOff, Check, X, AlertCircle, Loader2, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  salades: { bases: 'Bases', ingredients: 'Ingrédients', condiments: 'Condiments', sauces: 'Sauces' },
  pates: { types: 'Types de pâtes', sauces: 'Sauces', condiments: 'Condiments', extras: 'Extras (+prix)' },
};

const SUBCATEGORY_LABELS = {
  salade: 'Salade', grain: 'Grain', legumineuse: 'Légumineuse', chaud: 'Chaud',
  froid: 'Légumes froids', proteine_froide: 'Protéines froides',
  proteine_chaude: 'Protéines chaudes', fromage: 'Fromages',
  fondante: 'Fondante', al_dente: 'Al dente',
};

const ALLERGEN_META = [
  { id: 'gluten', icon: '🌾', label: 'Gluten', color: '#c8a056' },
  { id: 'crustaces', icon: '🦐', label: 'Crustacés', color: '#e07050' },
  { id: 'oeufs', icon: '🥚', label: 'Œufs', color: '#EEB52D' },
  { id: 'poissons', icon: '🐟', label: 'Poissons', color: '#4a8ec2' },
  { id: 'arachides', icon: '🥜', label: 'Arachides', color: '#a07848' },
  { id: 'soja', icon: '🌱', label: 'Soja', color: '#6aab80' },
  { id: 'lait', icon: '🥛', label: 'Lait', color: '#5a9ad4' },
  { id: 'fruitsacoque', icon: '🌰', label: 'Fruits à coque', color: '#8B6040' },
  { id: 'celeri', icon: '🌿', label: 'Céleri', color: '#4a8a60' },
  { id: 'moutarde', icon: '🟡', label: 'Moutarde', color: '#c8a020' },
  { id: 'sesame', icon: '⚫', label: 'Sésame', color: '#5a5a5a' },
  { id: 'sulfites', icon: '🍷', label: 'Sulfites', color: '#8a4a7a' },
  { id: 'mollusques', icon: '🐚', label: 'Mollusques', color: '#5a8090' },
  { id: 'lupin', icon: '🟣', label: 'Lupin', color: '#7060b0' },
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem('grainette_admin_token') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('salades');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Menu photo import
  const [importFile, setImportFile] = useState(null);
  const [importCategory, setImportCategory] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedImports, setSelectedImports] = useState({});
  const [importSuccess, setImportSuccess] = useState(null);

  // Allergen state
  const [allergenItems, setAllergenItems] = useState([]);
  const [allergenSearch, setAllergenSearch] = useState('');
  const [allergenFile, setAllergenFile] = useState(null);
  const [allergenLoading, setAllergenLoading] = useState(false);
  const [allergenImportResults, setAllergenImportResults] = useState(null);
  const [allergenImportSelected, setAllergenImportSelected] = useState({});
  const [allergenSuccess, setAllergenSuccess] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const r = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) { setLoginError('Mot de passe incorrect'); return; }
      const { token: t } = await r.json();
      localStorage.setItem('grainette_admin_token', t);
      setToken(t);
    } catch { setLoginError('Erreur de connexion'); }
  };

  const logout = () => { localStorage.removeItem('grainette_admin_token'); setToken(''); };

  const loadItems = useCallback(async () => {
    if (!token || tab === 'allergenes') return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/menu/${tab}`, { headers: authHeaders });
      if (r.status === 401) { logout(); return; }
      setItems(await r.json());
    } finally { setLoading(false); }
  }, [token, tab]);

  const loadAllergens = useCallback(async () => {
    if (!token || tab !== 'allergenes') return;
    const r = await fetch(`${API}/api/admin/allergens`, { headers: authHeaders });
    if (r.ok) setAllergenItems(await r.json());
  }, [token, tab]);

  useEffect(() => { loadItems(); loadAllergens(); }, [loadItems, loadAllergens]);

  const toggleItem = async (item_id) => {
    await fetch(`${API}/api/admin/menu/${item_id}/toggle`, { method: 'PATCH', headers: authHeaders });
    loadItems();
  };

  const deleteItem = async (item_id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await fetch(`${API}/api/admin/menu/${item_id}`, { method: 'DELETE', headers: authHeaders });
    loadItems();
  };

  // ── Menu photo import ─────────────────────────────────────────────────────

  const handleMenuImport = async () => {
    if (!importFile || !importCategory) return;
    setImportLoading(true); setImportResults(null); setImportSuccess(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile); fd.append('menu_type', tab); fd.append('category', importCategory);
      const r = await fetch(`${API}/api/admin/import-photo`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      setImportResults(data.detected || []);
      const sel = {};
      (data.detected || []).forEach((item, i) => { if (!item.is_duplicate) sel[i] = true; });
      setSelectedImports(sel);
    } catch { alert('Erreur lors de l\'analyse IA'); }
    finally { setImportLoading(false); }
  };

  const confirmMenuImport = async () => {
    const toAdd = importResults.filter((_, i) => selectedImports[i]);
    const catOrder = Object.keys(CATEGORY_LABELS[tab] || {}).indexOf(importCategory);
    for (let i = 0; i < toAdd.length; i++) {
      await fetch(`${API}/api/admin/menu`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ menu_type: tab, category: importCategory, name: toAdd[i].name, active: true, order: catOrder * 100 + i }),
      });
    }
    setImportSuccess(`${toAdd.length} produit(s) ajouté(s) !`);
    setImportResults(null); setImportFile(null); loadItems();
  };

  // ── Allergen photo import ─────────────────────────────────────────────────

  const handleAllergenImport = async () => {
    if (!allergenFile) return;
    setAllergenLoading(true); setAllergenImportResults(null); setAllergenSuccess(null);
    try {
      const fd = new FormData();
      fd.append('file', allergenFile);
      const r = await fetch(`${API}/api/admin/import-photo-allergens`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      setAllergenImportResults(data.detected || []);
      const sel = {};
      (data.detected || []).forEach((item, i) => { sel[i] = true; });
      setAllergenImportSelected(sel);
    } catch { alert('Erreur lors de l\'analyse IA'); }
    finally { setAllergenLoading(false); }
  };

  const confirmAllergenImport = async () => {
    const toSave = allergenImportResults.filter((_, i) => allergenImportSelected[i]);
    for (const item of toSave) {
      await fetch(`${API}/api/admin/allergens`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ product: item.product, concept: item.concept, allergens: item.allergens }),
      });
    }
    setAllergenSuccess(`${toSave.length} fiche(s) allergènes enregistrée(s) !`);
    setAllergenImportResults(null); setAllergenFile(null); loadAllergens();
  };

  const deleteAllergenItem = async (product, concept) => {
    if (!window.confirm('Supprimer la fiche allergènes de ce produit ?')) return;
    // Use upsert with empty allergens to effectively clear
    await fetch(`${API}/api/admin/allergens`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ product, concept, allergens: [] }),
    });
    loadAllergens();
  };

  // ── Group items ───────────────────────────────────────────────────────────

  const filtered = (search
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items
  );
  const grouped = filtered.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const filteredAllergens = allergenSearch
    ? allergenItems.filter(i => i.product.toLowerCase().includes(allergenSearch.toLowerCase()))
    : allergenItems;

  const categories = CATEGORY_LABELS[tab] || {};

  // ── Login ─────────────────────────────────────────────────────────────────

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#2A3630] flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-black text-xl" style={{ fontFamily: 'Barlow, sans-serif' }}>G</span>
            </div>
            <h1 className="text-2xl font-black text-[#2A3630]" style={{ fontFamily: 'Barlow, sans-serif' }}>Admin Grainette</h1>
            <p className="text-sm text-gray-400 mt-1">Espace de gestion des menus</p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe" data-testid="admin-password-input"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#80a890] bg-gray-50 focus:bg-white transition-colors" />
            {loginError && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{loginError}</p>}
            <button type="submit" data-testid="admin-login-btn"
              className="w-full py-3 rounded-xl font-bold text-sm text-white" style={{ backgroundColor: '#2A3630' }}>
              Connexion
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-dashboard">
      <div className="bg-[#2A3630] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-white font-black text-lg" style={{ fontFamily: 'Barlow, sans-serif' }}>Admin Grainette</span>
          <span className="text-xs text-white/40">Espace privé</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-xs text-white/60 hover:text-white transition-colors">← Voir le site</a>
          <button onClick={logout} className="text-xs text-white/60 hover:text-white flex items-center gap-1 transition-colors">
            <LogOut size={13} /> Déconnexion
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-100 px-6">
        <div className="flex gap-1">
          {[['salades', 'Menu Salades'], ['pates', 'Menu Pâtes'], ['allergenes', 'Allergènes']].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setImportResults(null); setAllergenImportResults(null); setSearch(''); }}
              data-testid={`admin-tab-${key}`}
              className="px-5 py-4 text-sm font-bold border-b-2 transition-colors"
              style={{ borderColor: tab === key ? '#80a890' : 'transparent', color: tab === key ? '#2A3630' : '#9ca3af' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* ── ALLERGEN TAB ─────────────────────────────────────────────── */}
        {tab === 'allergenes' && (
          <>
            {/* Photo import */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Upload size={16} className="text-[#80a890]" />
                <h2 className="font-black text-sm text-[#2A3630]" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  Import fiche allergènes par photo IA
                </h2>
                <span className="text-xs text-gray-400 ml-1">— photo de votre tableau allergènes papier</span>
              </div>
              <div className="p-6">
                {allergenSuccess && (
                  <div className="mb-4 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
                    <Check size={14} /> {allergenSuccess}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Photo / Tableau PDF scanné</label>
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#80a890] cursor-pointer transition-colors text-sm text-gray-400">
                      <Upload size={14} />
                      {allergenFile ? allergenFile.name : 'Choisir une image'}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        data-testid="allergen-file-input"
                        onChange={e => { setAllergenFile(e.target.files[0]); setAllergenImportResults(null); }} />
                    </label>
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleAllergenImport} disabled={!allergenFile || allergenLoading}
                      data-testid="allergen-analyze-btn"
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 flex items-center gap-2"
                      style={{ backgroundColor: '#80a890' }}>
                      {allergenLoading ? <><Loader2 size={14} className="animate-spin" />Analyse IA...</> : 'Analyser'}
                    </button>
                  </div>
                </div>

                {/* Allergen import results */}
                {allergenImportResults && (
                  <div className="mt-6 space-y-3" data-testid="allergen-import-results">
                    <p className="text-sm font-semibold text-[#2A3630]">
                      {allergenImportResults.length} produit(s) détecté(s)
                    </p>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {allergenImportResults.map((item, i) => (
                        <label key={i} className={`flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${allergenImportSelected[i] ? 'border-[#80a890] bg-[#f0f8f4]' : 'border-gray-200 opacity-60'}`}>
                          <input type="checkbox" checked={!!allergenImportSelected[i]}
                            onChange={e => setAllergenImportSelected({ ...allergenImportSelected, [i]: e.target.checked })}
                            className="accent-[#80a890]" />
                          <span className="text-sm font-semibold text-[#2A3630] flex-shrink-0">{item.product}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                            style={{ background: item.concept === 'pates' ? 'rgba(238,181,45,0.15)' : 'rgba(128,168,144,0.15)',
                              color: item.concept === 'pates' ? '#b88a00' : '#2A6030' }}>
                            {item.concept === 'pates' ? 'Pâtes' : 'Salades'}
                          </span>
                          {item.is_duplicate && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">doublon</span>}
                          <div className="flex flex-wrap gap-1 mt-1 w-full">
                            {item.allergens.map(aId => {
                              const meta = ALLERGEN_META.find(a => a.id === aId);
                              return meta ? (
                                <span key={aId} className="text-xs px-2 py-0.5 rounded-full font-medium"
                                  style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}>
                                  {meta.icon} {meta.label}
                                </span>
                              ) : null;
                            })}
                            {item.allergens.length === 0 && <span className="text-xs text-gray-400">Aucun allergène détecté</span>}
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button onClick={() => setAllergenImportResults(null)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
                      <button onClick={confirmAllergenImport}
                        disabled={!Object.values(allergenImportSelected).some(Boolean)}
                        data-testid="allergen-confirm-btn"
                        className="px-5 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-40"
                        style={{ backgroundColor: '#2A3630' }}>
                        Enregistrer {Object.values(allergenImportSelected).filter(Boolean).length} produit(s)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Saved allergens */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                <h2 className="font-black text-sm text-[#2A3630] shrink-0" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  Allergènes enregistrés ({allergenItems.length})
                </h2>
                <div className="relative flex-1 max-w-xs">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={allergenSearch} onChange={e => setAllergenSearch(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:border-[#80a890]" />
                </div>
              </div>
              <div className="p-6 space-y-2">
                {filteredAllergens.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    {allergenSearch ? `Aucun résultat pour "${allergenSearch}"` : 'Aucun allergène enregistré — importez une fiche ci-dessus.'}
                  </p>
                )}
                {filteredAllergens.map((item, idx) => (
                  <div key={idx} className="flex flex-wrap items-start gap-2 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50">
                    <span className="text-sm font-semibold text-[#2A3630]">{item.product}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: item.concept === 'pates' ? 'rgba(238,181,45,0.12)' : 'rgba(128,168,144,0.12)',
                        color: item.concept === 'pates' ? '#b88a00' : '#2A6030' }}>
                      {item.concept === 'pates' ? 'Pâtes' : 'Salades'}
                    </span>
                    <div className="flex flex-wrap gap-1 flex-1">
                      {(item.allergens || []).map(aId => {
                        const meta = ALLERGEN_META.find(a => a.id === aId);
                        return meta ? (
                          <span key={aId} className="text-xs px-2 py-0.5 rounded-full"
                            style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}30` }}>
                            {meta.icon} {meta.label}
                          </span>
                        ) : null;
                      })}
                      {(!item.allergens || item.allergens.length === 0) && <span className="text-xs text-gray-400 italic">Aucun allergène</span>}
                    </div>
                    <button onClick={() => deleteAllergenItem(item.product, item.concept)}
                      className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors text-gray-400 ml-auto">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── MENU TABS ─────────────────────────────────────────────────── */}
        {tab !== 'allergenes' && (
          <>
            {/* Photo import */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Upload size={16} className="text-[#80a890]" />
                <h2 className="font-black text-sm text-[#2A3630]" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  Import par photo IA
                </h2>
                <span className="text-xs text-gray-400 ml-1">— glissez une photo de votre liste de produits</span>
              </div>
              <div className="p-6">
                {importSuccess && (
                  <div className="mb-4 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
                    <Check size={14} />{importSuccess}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Catégorie cible</label>
                    <select value={importCategory} onChange={e => setImportCategory(e.target.value)}
                      data-testid="import-category-select"
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-[#80a890]">
                      <option value="">— Choisir —</option>
                      {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Photo / Screenshot</label>
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#80a890] cursor-pointer transition-colors text-sm text-gray-400">
                      <Upload size={14} />
                      {importFile ? importFile.name : 'Choisir une image'}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        data-testid="import-file-input"
                        onChange={e => { setImportFile(e.target.files[0]); setImportResults(null); }} />
                    </label>
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleMenuImport} disabled={!importFile || !importCategory || importLoading}
                      data-testid="import-analyze-btn"
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 flex items-center gap-2"
                      style={{ backgroundColor: '#80a890' }}>
                      {importLoading ? <><Loader2 size={14} className="animate-spin" />Analyse IA...</> : 'Analyser'}
                    </button>
                  </div>
                </div>

                {importResults && (
                  <div className="mt-6 space-y-3" data-testid="import-results">
                    <p className="text-sm font-semibold text-[#2A3630]">
                      {importResults.length} produit(s) détecté(s)
                      <span className="text-gray-400 font-normal ml-2">— {importResults.filter(i => i.is_duplicate).length} doublon(s)</span>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                      {importResults.map((item, i) => (
                        <label key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors ${selectedImports[i] ? 'border-[#80a890] bg-[#f0f8f4]' : 'border-gray-200 bg-white opacity-60'}`}>
                          <input type="checkbox" checked={!!selectedImports[i]}
                            onChange={e => setSelectedImports({ ...selectedImports, [i]: e.target.checked })}
                            className="accent-[#80a890]" />
                          <span className="text-sm text-[#2A3630] flex-1">{item.name}</span>
                          {item.is_duplicate
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">doublon</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">nouveau</span>}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button onClick={() => setImportResults(null)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Annuler</button>
                      <button onClick={confirmMenuImport} disabled={!Object.values(selectedImports).some(Boolean)}
                        data-testid="import-confirm-btn"
                        className="px-5 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-40"
                        style={{ backgroundColor: '#2A3630' }}>
                        Ajouter {Object.values(selectedImports).filter(Boolean).length} produit(s)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Products list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                <h2 className="font-black text-sm text-[#2A3630] shrink-0" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  Produits actuels ({items.length})
                </h2>
                <div className="relative flex-1 max-w-xs">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Rechercher..." data-testid="admin-search-input"
                    className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:border-[#80a890]" />
                </div>
              </div>
              {loading ? (
                <div className="p-12 text-center"><Loader2 size={24} className="animate-spin text-gray-300 mx-auto" /></div>
              ) : (
                <div className="p-6 space-y-6">
                  {Object.entries(grouped).map(([cat, catItems]) => (
                    <div key={cat}>
                      <p className="text-xs font-black uppercase tracking-wider text-gray-400 mb-3" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        {CATEGORY_LABELS[tab]?.[cat] || cat}
                      </p>
                      <div className="space-y-1.5">
                        {catItems.map(item => (
                          <div key={item.item_id}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-colors ${item.active ? 'border-gray-100 bg-gray-50' : 'border-gray-100 bg-white opacity-40'}`}>
                            <span className="flex-1 text-sm text-[#2A3630]">{item.name}</span>
                            {item.subcategory && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                {SUBCATEGORY_LABELS[item.subcategory] || item.subcategory}
                              </span>
                            )}
                            {item.price != null && <span className="text-xs font-bold text-[#EEB52D]">+{item.price.toFixed(2)} €</span>}
                            <button onClick={() => toggleItem(item.item_id)} title={item.active ? 'Désactiver' : 'Activer'}
                              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-400">
                              {item.active ? <Eye size={14} /> : <EyeOff size={14} />}
                            </button>
                            <button onClick={() => deleteItem(item.item_id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors text-gray-400">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {Object.keys(grouped).length === 0 && !loading && (
                    <p className="text-sm text-gray-400 text-center py-6">
                      {search ? `Aucun produit pour "${search}"` : 'Aucun produit trouvé'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
